import React from 'react';
import { X, MapPin, Package, Truck, CheckCircle } from 'lucide-react';

const OrderDetailModal = ({ isOpen, onClose, order }) => {
  if (!isOpen || !order) return null;

  // Mock tracking timeline data
  const trackingTimeline = [
    {
      status: 'Order Confirmed',
      location: 'Oakwood Drive, Dallas, TX',
      date: 'Nov 16, 2024',
      active: false
    },
    {
      status: 'Package Received',
      location: 'Distribution Center, Houston, TX',
      date: 'Nov 17, 2024',
      active: false
    },
    {
      status: 'In Transit',
      location: 'Regional Hub, Austin, TX',
      date: 'Nov 18, 2024',
      active: false
    },
    {
      status: 'In Delivery To Destination',
      location: 'Local Facility, Enterprise, AL',
      date: 'Nov 19, 2024',
      active: true
    },
    {
      status: 'Delivered',
      location: order.address,
      date: 'Expected Nov 20, 2024',
      active: false
    }
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80 z-[70] backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-4 md:left-[17rem] z-[70] overflow-hidden">
        <div className="h-full bg-black border-2 border-white/40 rounded-2xl shadow-[0_0_50px_rgba(255,255,255,0.3)] flex flex-col">
          {/* Modal Header */}
          <div className="p-6 border-b border-white/20">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-1">Order Tracking</h2>
                <p className="text-sm text-gray-400">Track your order status</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-800 rounded-lg transition-all"
              >
                <X size={24} className="text-gray-400 hover:text-white" />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 gap-6 p-6">
              {/* Left Column - Order Details */}
              <div className="space-y-6">
                {/* Order ID */}
                <div className="bg-black border border-white/40 rounded-xl p-4 hover:border-white/60 transition-all">
                  <div className="text-xs text-gray-400 mb-1">Order ID Detail</div>
                  <div className="text-lg font-bold">{order.id}</div>
                </div>

                {/* Created Date */}
                <div>
                  <div className="text-xs text-gray-400 mb-2">Created Date</div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm">{order.date}</div>
                    <div className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                      {order.payment}
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="bg-black border border-white/40 rounded-xl p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
                  <h3 className="text-sm font-semibold mb-4">Order Items</h3>
                  
                  {/* Product */}
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <div className="w-24 h-24 bg-gray-900 rounded-lg flex items-center justify-center overflow-hidden">
                      <Package size={40} className="text-gray-600" />
                    </div>

                    {/* Product Details */}
                    <div className="flex-1">
                      <h4 className="font-semibold mb-1">{order.product}</h4>
                      <p className="text-xs text-gray-400 mb-3">Product Category</p>
                      
                      {/* Specs */}
                      <div className="flex gap-2 mb-3">
                        <span className="px-3 py-1 bg-gray-800 rounded-lg text-xs">Standard</span>
                        <span className="px-3 py-1 bg-gray-800 rounded-lg text-xs">x{order.id.split('-')[1]?.slice(-2) || '1'}</span>
                      </div>

                      {/* Price */}
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold">{order.price}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment */}
                <div className="bg-black border border-white/40 rounded-xl p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
                  <h3 className="text-sm font-semibold mb-4">Payment</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Subtotal</span>
                      <span className="font-medium">{order.price}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Shipping</span>
                      <span className="font-medium">$38.00</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Estimated Taxes</span>
                      <span className="font-medium">$80.00</span>
                    </div>
                    <div className="pt-3 border-t border-white/20">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">Total</span>
                        <span className="text-xl font-bold">
                          ${(parseFloat(order.price.replace('$', '').replace(',', '')) + 118).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Order Tracking */}
              <div className="space-y-6">
                {/* Map Placeholder */}
                <div className="bg-black border border-white/40 rounded-xl p-4 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
                  <div className="h-64 bg-gray-900 rounded-lg relative overflow-hidden">
                    {/* Map Background */}
                    <div className="absolute inset-0 opacity-20">
                      <svg viewBox="0 0 400 300" className="w-full h-full">
                        {/* Simplified map visualization */}
                        <path d="M 50 150 Q 150 100, 250 150 T 350 150" 
                          stroke="rgb(59, 130, 246)" 
                          strokeWidth="3" 
                          fill="none" 
                          strokeLinecap="round"
                        />
                        <circle cx="50" cy="150" r="6" fill="rgb(59, 130, 246)" />
                        <circle cx="250" cy="150" r="6" fill="rgb(59, 130, 246)" />
                        <circle cx="350" cy="150" r="8" fill="rgb(59, 130, 246)">
                          <animate attributeName="r" values="8;12;8" dur="2s" repeatCount="indefinite" />
                        </circle>
                      </svg>
                    </div>
                    
                    {/* Map Icon Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <MapPin size={48} className="text-blue-500" />
                    </div>
                  </div>
                </div>

                {/* Tracking Timeline */}
                <div className="bg-black border border-white/40 rounded-xl p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
                  <h3 className="text-sm font-semibold mb-6">Order Tracking</h3>
                  
                  <div className="space-y-4">
                    {trackingTimeline.map((item, index) => (
                      <div key={index} className="flex gap-4">
                        {/* Timeline Dot */}
                        <div className="relative">
                          <div className={`w-3 h-3 rounded-full ${
                            item.active ? 'bg-blue-500' : 
                            index < trackingTimeline.findIndex(t => t.active) ? 'bg-green-500' : 'bg-gray-600'
                          }`} />
                          {index < trackingTimeline.length - 1 && (
                            <div className="absolute top-3 left-1.5 w-0.5 h-8 bg-gray-700" />
                          )}
                        </div>

                        {/* Timeline Content */}
                        <div className="flex-1 pb-2">
                          <div className={`text-sm font-medium mb-1 ${
                            item.active ? 'text-blue-400' : 'text-white'
                          }`}>
                            {item.status}
                          </div>
                          <div className="text-xs text-gray-400 mb-1">{item.location}</div>
                          <div className="text-xs text-gray-500">{item.date}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetailModal;
